import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  Paper,
  Slide,
  Grid,
  Divider,
  Container,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import useApiEp from "../../hooks/useApiEp";

// Consistent color palette with good contrast
const STATUS_COLORS = {
  Queued: "#FFA726", // Orange 400
  Cancelled: "#EF5350", // Red 400
  Sent: "#42A5F5", // Blue 400
  Delivered: "#66BB6A", // Green 400
  Read: "#AB47BC", // Purple 400
  Failed: "#78909C", // Blue Grey 400
};

// Colors for pie chart that match the status colors
const PIE_COLORS = Object.values(STATUS_COLORS);

const CampaignInfo = ({ campaignName, startDate, endDate }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { data, isLoading, isError, error } = useApiEp(
    (() => {
      const params = new URLSearchParams();
      if (campaignName) {
        params.append("campaignName", encodeURIComponent(campaignName));
      }
      if (startDate && endDate) {
        params.append("startDate", encodeURIComponent(startDate));
        params.append("endDate", encodeURIComponent(endDate));
      }
      return `/api/get-campaign-stats/?${params.toString()}`;
    })()
  );

  const handleToggle = () => {
    setShowInfo(!showInfo);
  };

  // Transform API data into chart-compatible format with consistent colors
  const campaignData = data?.data
    ? [
        {
          name: "Queued",
          value: data.data.queued || 0,
          color: STATUS_COLORS.Queued,
        },
        {
          name: "Cancelled",
          value: data.data.cancelled || 0,
          color: STATUS_COLORS.Cancelled,
        },
        { name: "Sent", value: data.data.sent || 0, color: STATUS_COLORS.Sent },
        {
          name: "Delivered",
          value: data.data.delivered || 0,
          color: STATUS_COLORS.Delivered,
        },
        { name: "Read", value: data.data.read || 0, color: STATUS_COLORS.Read },
        {
          name: "Failed",
          value: data.data.failed || 0,
          color: STATUS_COLORS.Failed,
        },
      ]
    : [];

  // Custom label for pie chart to show percentage
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

    return (
      <text
        x={x}
        y={y}
        fill="white" // Changed to white for better contrast on colored segments
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontWeight: "bold", fontSize: "12px" }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 1, mx: "auto" }}>
      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleToggle}
          sx={{
            backgroundColor: "#1976D2",
            "&:hover": { backgroundColor: "#1565C0" },
          }}
        >
          {showInfo ? "Hide Campaign Stats" : "Show Campaign Stats"}
        </Button>
      </Box>

      <Slide direction="up" in={showInfo} mountOnEnter unmountOnExit>
        <Paper
          elevation={3}
          sx={{ p: 3, borderRadius: 2, backgroundColor: "#f5f5f5" }}
        >
          <Typography variant="h5" gutterBottom color="text.primary">
            Campaign Message Statistics
          </Typography>
          <Divider sx={{ mb: 2, borderColor: "rgba(0, 0, 0, 0.12)" }} />

          {isLoading && <Typography>Loading...</Typography>}
          {isError && (
            <Typography color="error">Error: {error?.message}</Typography>
          )}
          {!isLoading && !isError && campaignData.length === 0 && (
            <Typography>No data available</Typography>
          )}

          {campaignData.length > 0 && (
            <Grid container spacing={2}>
              {/* Stats Summary */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Summary
                </Typography>
                {campaignData.map((item) => (
                  <Box
                    key={item.name}
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <Chip
                      label={`${item.name}: ${item.value}`}
                      sx={{
                        backgroundColor: item.color,
                        color: "white",
                        fontWeight: "bold",
                        mr: 1,
                        minWidth: 120,
                      }}
                    />
                    <LinearProgress
                      variant="determinate"
                      value={
                        (item.value /
                          Math.max(...campaignData.map((i) => i.value), 1)) *
                        100
                      }
                      sx={{
                        flexGrow: 1,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: `${item.color}20`, // Adds 20% opacity
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: item.color,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Grid>

              {/* Bar Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Message Status Distribution
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={campaignData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" stroke="#333" />
                      <YAxis stroke="#333" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar dataKey="value">
                        {campaignData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Pie Chart */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Engagement Breakdown
                </Typography>
                <Box sx={{ height: 250 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={campaignData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        dataKey="value"
                        label={renderCustomizedLabel}
                        labelLine={false}
                      >
                        {campaignData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="#fff"
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span style={{ color: "#333" }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Slide>
    </Container>
  );
};

export default CampaignInfo;
